import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { RootState } from '../../store';
import { useUser, useUserRoles } from '../session/hooks';
import {
  checkPermission,
  checkRole,
  Permission,
  Role,
} from '@pcmp-checka/lib-utilities/atkomst';
import {
  Location,
  Maybe,
  useLocationQuery,
  useLocationsQuery,
} from '@/redux/back-dashboard-api';
import { QUERY_LIMIT } from '@/lib/constants';
import { _updateShoppingBag } from './slice';

export const useShoppingBag = () =>
  useAppSelector((state: RootState) => state.shoppingBagSlice);

export const useActiveLocation = () => {
  // Hooks
  const dispatch = useAppDispatch();
  // Data
  const user = useUser();
  const shoppingBag = useShoppingBag();
  const {
    data,
    isLoading: isLoadingLocations,
    error: errorLocations,
  } = useLocationsQuery({
    pagination: { limit: QUERY_LIMIT },
  });
  const selectedLocationId = shoppingBag.location;
  const defaultLocation = user.officeLocation;
  const location = useMemo(() => {
    if (!selectedLocationId) {
      // No explicit selection: only auto-pick the office default, never a
      // fallback — an unmatched default should surface "no location
      // selected" (see ActiveLocationChecker / SelectLocationLink), not
      // silently pick an arbitrary location.
      return data?.locations.nodes.find(({ cdsLocation }) =>
        cdsLocation?.some((entry) => entry.name === defaultLocation)
      );
    }

    // ponytail: an explicit selection (persisted in localStorage) that no
    // longer matches any location (deleted/deactivated) falls back to the
    // first available one, rather than leaving the shop with no active
    // location at all.
    return (
      data?.locations.nodes.find(({ _id }) => _id === selectedLocationId) ??
      data?.locations.nodes[0]
    );
  }, [data, defaultLocation, selectedLocationId]);
  const {
    data: locationDetail,
    isLoading: isLoadingLocation,
    error: errorLocation,
  } = useLocationQuery(
    {
      locationId: location?._id || '',
    },
    {
      skip: !location?._id,
    }
  );
  const isLoading = isLoadingLocations || isLoadingLocation;
  const error = errorLocations || errorLocation;

  // If no location is selected in the shopping bag, set it to the found location
  useEffect(() => {
    if (!shoppingBag.location && locationDetail?.location) {
      dispatch(_updateShoppingBag({ location: locationDetail.location._id }));
    }
  }, [locationDetail, shoppingBag.location, dispatch]);

  return useMemo(
    () => ({ data: locationDetail?.location, isLoading, error }),
    [locationDetail, isLoading, error]
  );
};

const useLocationPermission = (
  permission: Permission,
  location?: Maybe<Location>
) => {
  const roles = useUserRoles();

  return useMemo(
    () =>
      location
        ? checkPermission(
            roles,
            permission,
            location.atkomstClientId,
            location.country
          )
        : false,
    [permission, location, roles]
  );
};

const useLocationRole = (role: Role, location?: Maybe<Location>) => {
  const roles = useUserRoles();

  return useMemo(
    () =>
      location
        ? checkRole(roles, role, location.atkomstClientId, location.country)
        : false,
    [role, location, roles]
  );
};

export const useActiveLocationPermissions = () => {
  const { data: location } = useActiveLocation();
  const roles = useUserRoles();
  // Permissions
  const canCreateOrders = useLocationPermission('order.create', location);
  const canUpdateOrders = useLocationPermission('order.update', location);
  const canOrderOnBehalf = useLocationPermission(
    'order.create-on-behalf',
    location
  );
  const canImportInventory = useLocationPermission(
    'order.import-inventory',
    location
  );
  // Roles
  const isOrderAdmin = useLocationRole('CHECKA Order Admin', location);
  const isStockAdmin = useLocationRole('CHECKA Stock Admin', location);
  const isUserAdmin = useLocationRole('CHECKA User Admin', location);
  const isLocationAdmin = useLocationRole('CHECKA Location Admin', location);
  const isSuperAdmin = checkRole(
    roles,
    'CHECKA Location Admin',
    'PLATFORM_OWNER',
    'PLATFORM_OWNER'
  );

  return useMemo(
    () => ({
      canCreateOrders,
      canUpdateOrders,
      canOrderOnBehalf,
      canImportInventory,
      isOrderAdmin,
      isStockAdmin,
      isUserAdmin,
      isLocationAdmin,
      isAdmin: isOrderAdmin || isStockAdmin || isUserAdmin || isLocationAdmin,
      isSuperAdmin,
    }),
    [
      canCreateOrders,
      canUpdateOrders,
      canOrderOnBehalf,
      canImportInventory,
      isOrderAdmin,
      isStockAdmin,
      isUserAdmin,
      isLocationAdmin,
      isSuperAdmin,
    ]
  );
};
